/* eslint-disable react/no-multi-comp */
import React, { useContext, useEffect, useState } from 'react';
import { Redirect, Switch } from 'react-router-dom';

import ScrollToTop from './common/ScrollToTop';
import { RouteWithLayout } from './components';
import { Main as MainLayout, Minimal as MinimalLayout } from './layouts';

import {
  AccountView,
  CreateWorkoutPlanView,
  DashboardView,
  HomeView,
  IconsView,
  NotFoundView,
  PrivacyView,
  ProductListView,
  RecoverPasswordView,
  RunnersListView,
  SettingsView,
  SignInView,
  SignUpView,
  TemplateDashboardView,
  TypographyView,
  UserListView,
  WorkoutPlanView,
  TemplatesView,
  PaymentView
} from './views';

import server from 'api/server';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { Box, CircularProgress } from '@material-ui/core';
import UserContext from 'context/User';
import SetUserLoadedContext from 'context/SetUserLoaded/SetUserLoadedContext';
import PreferencesContext from 'context/Preferences';
import Preferences from 'classes/Preferences';

const preferences = new Preferences(server);

const Routes = () => {
  const [user, setUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);

  useEffect(() => {
    const setupUser = async () => {
      const auth = getAuth();
      onAuthStateChanged(auth, loggedUser => {
        setUserLoaded(false);
        if (loggedUser) {
          // User is signed in, see docs for a list of available properties
          // https://firebase.google.com/docs/reference/js/firebase.User
          // const uid = user.uid;
          server.defaults.headers.common['Authorization'] =
            'Bearer ' + loggedUser.accessToken;
          server
            .get(`/isUser?email=${loggedUser.email}`, {
              headers: { Authorization: 'Bearer ' + loggedUser.accessToken }
            })
            .then(userDataFromDB => {
              setUser({ ...loggedUser, ...userDataFromDB.data.value });
              preferences.loadUserPreferences();
              setUserLoaded(true);
            });
        } else {
          setUser(null);
          setUserLoaded(true);
        }
      });
    };

    setupUser();

    return () => {};
  }, []);

  return (
    <UserContext.Provider value={user}>
      <SetUserLoadedContext.Provider value={setUserLoaded}>
        <PreferencesContext.Provider value={preferences}>
          <RoutesElement
            user={user}
            setUser={setUser}
            userLoaded={userLoaded}
          />
        </PreferencesContext.Provider>
      </SetUserLoadedContext.Provider>
    </UserContext.Provider>
  );
};

const RoutesElement = ({ user, setUser, userLoaded }) => {
  if (user && userLoaded) {
    if (user.role === 'trainer') {
      if (
        !user.activeUntilDate ||
        new Date(user.activeUntilDate) < new Date()
      ) {
        return (
          <ScrollToTop>
            <Switch>
              <RouteWithLayout
                component={PaymentView}
                exact
                layout={MinimalLayout}
                path="/payment"
              />
              <Redirect to="/payment" />
            </Switch>
          </ScrollToTop>
        );
      }
    }

    return (
      <ScrollToTop>
        <Switch>
          <Redirect exact from="/" to="/workoutPlan" />
          <Redirect exact from="/sign-in" to="/workoutPlan" />
          <Redirect exact from="/sign-up" to="/workoutPlan" />
          <RouteWithLayout
            component={DashboardView}
            exact
            layout={MainLayout}
            path="/dashboard"
          />
          <RouteWithLayout
            component={RunnersListView}
            exact
            layout={MainLayout}
            path="/runnersList"
          />
          <RouteWithLayout
            component={CreateWorkoutPlanView}
            exact
            layout={MainLayout}
            path="/createWorkoutPlan"
          />
          <RouteWithLayout
            component={WorkoutPlanView}
            exact
            layout={MainLayout}
            path="/workoutPlan"
          />
          <RouteWithLayout
            component={TemplatesView}
            exact
            layout={MainLayout}
            path="/templates"
          />
          <RouteWithLayout
            component={TemplateDashboardView}
            exact
            layout={MainLayout}
            path="/templateDashboard"
          />
          <RouteWithLayout
            component={UserListView}
            exact
            layout={MainLayout}
            path="/users"
          />
          <RouteWithLayout
            component={ProductListView}
            exact
            layout={MainLayout}
            path="/products"
          />
          <RouteWithLayout
            component={TypographyView}
            exact
            layout={MainLayout}
            path="/typography"
          />
          <RouteWithLayout
            component={IconsView}
            exact
            layout={MainLayout}
            path="/icons"
          />
          <RouteWithLayout
            component={AccountView}
            exact
            layout={MainLayout}
            path="/account"
          />
          <RouteWithLayout
            component={SettingsView}
            exact
            layout={MainLayout}
            path="/settings"
          />
          <RouteWithLayout
            component={SignInView}
            exact
            layout={MinimalLayout}
            path="/sign-in"
            setUser={setUser}
          />
          <RouteWithLayout
            component={RecoverPasswordView}
            exact
            layout={MinimalLayout}
            path="/recover-password"
          />
          <RouteWithLayout
            component={SignUpView}
            exact
            layout={MinimalLayout}
            path="/sign-up/:runnerId"
            setUser={setUser}
          />
          <RouteWithLayout
            component={SignUpView}
            exact
            layout={MinimalLayout}
            path="/sign-up"
            setUser={setUser}
          />
          <RouteWithLayout
            component={NotFoundView}
            exact
            layout={MinimalLayout}
            path="/not-found"
          />
          <Redirect to="/not-found" />
        </Switch>
      </ScrollToTop>
    );
  } else if (!user && userLoaded) {
    return (
      <ScrollToTop>
        <Switch>
          <HomeView exact path="/" />
          <RouteWithLayout
            component={SignInView}
            exact
            layout={MinimalLayout}
            path="/sign-in"
            setUser={setUser}
          />
          <RouteWithLayout
            component={RecoverPasswordView}
            exact
            layout={MinimalLayout}
            path="/recover-password"
          />
          <RouteWithLayout
            component={SignUpView}
            exact
            layout={MinimalLayout}
            path="/sign-up/:registerId"
            setUser={setUser}
          />
          <RouteWithLayout
            component={SignUpView}
            exact
            layout={MinimalLayout}
            path="/sign-up"
            setUser={setUser}
          />
          <RouteWithLayout
            component={PrivacyView}
            exact
            layout={MinimalLayout}
            path="/privacy"
          />
          <RouteWithLayout
            component={NotFoundView}
            exact
            layout={MinimalLayout}
            path="/not-found"
          />
          <Redirect to="/" />
        </Switch>
      </ScrollToTop>
    );
  } else {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: '20px',
          height: '100vh'
        }}>
        <img src="images/logos/subcx_logo_blue.png" />
        <CircularProgress />
      </Box>
    );
  }
};

export default Routes;
